import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	InputAdornment,
	Paper,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import useStyles from "./styles";
import api from "../../../services/api";
import DatagridComponent from "../../../components/datagridComponent";
import corNeutraChip from "../corNeutraChip";

export default function ModalConfirmacao({
	open,
	onClose,
	item,
	clientesDesatualizados,
	onConcluido,
}) {
	const [carregandoDivergencias, setCarregandoDivergencias] = useState(true);
	const [divergencias, setDivergencias] = useState(new Map());
	const [selecionados, setSelecionados] = useState([]);
	const [confirmado, setConfirmado] = useState(false);
	const [busca, setBusca] = useState("");
	const [enviando, setEnviando] = useState(false);
	const [resumo, setResumo] = useState(null);
	const [erroEnvio, setErroEnvio] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	// Ao abrir, checa a divergencia de cada cliente desatualizado em paralelo - lista pequena,
	// so os clientes desatualizados de UM template, nao a base toda
	useEffect(() => {
		if (!open) return;
		buscarDivergencias();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// GET /versionamento/templates/divergencia por cliente - falha individual vira {erro: true},
	// tratado como bloqueado por seguranca (nunca deixa passar um cliente cuja divergencia nao foi possivel checar)
	async function buscarDivergencias() {
		setCarregandoDivergencias(true);
		setResumo(null);
		setErroEnvio(null);
		setConfirmado(false);
		try {
			const resultados = await Promise.all(
				(clientesDesatualizados || []).map(async (cliente) => {
					try {
						const response = await api.get("/versionamento/templates/divergencia", {
							params: { clienteId: cliente?.clienteId, chave: item?.chave },
						});
						return [cliente?.clienteId, response?.data];
					} catch {
						return [cliente?.clienteId, { erro: true }];
					}
				}),
			);
			const mapaDivergencias = new Map(resultados);
			setDivergencias(mapaDivergencias);
			// Selecao padrao: todo cliente nao bloqueado (e sem erro na checagem) comeca marcado
			const selecaoInicial = (clientesDesatualizados || [])
				.filter((cliente) => {
					const divergencia = mapaDivergencias.get(cliente?.clienteId);
					return divergencia && !divergencia?.erro && !divergencia?.bloqueia;
				})
				.map((cliente) => cliente?.clienteId);
			setSelecionados(selecaoInicial);
		} finally {
			setCarregandoDivergencias(false);
		}
	}

	// POST /versionamento/templates/atualizar - erro de rede/500 aqui e generico, nao se confunde
	// com falha individual de cliente (que ja vem dentro de resumo)
	async function handleConfirmarAtualizacao() {
		setEnviando(true);
		setErroEnvio(null);
		try {
			const response = await api.post("/versionamento/templates/atualizar", {
				chave: item?.chave,
				clienteIds: selecionados,
			});
			setResumo(response?.data?.resumo || []);
		} catch (error) {
			setErroEnvio(
				error?.response?.data?.error ||
					"Não foi possível enviar a atualização. Tente novamente.",
			);
		} finally {
			setEnviando(false);
		}
	}

	// Fecha o modal - se ja havia resultado de uma atualizacao enviada, dispara o refresh do pai antes
	function handleFechar() {
		if (enviando) return;
		if (resumo) {
			onConcluido?.();
		}
		onClose?.();
	}

	// Um cliente esta bloqueado quando a divergencia nao foi possivel checar (ou deu erro) ou quando
	// a propria divergencia bloqueia - reaproveitado no isRowSelectable e na celula "Bloqueado"
	function bloqueado(row) {
		const divergencia = divergencias.get(row?.clienteId);
		return !divergencia || divergencia?.erro || divergencia?.bloqueia;
	}

	// Motivo textual do bloqueio, usado no Tooltip e dentro do Chip "Bloqueado"
	function motivoBloqueio(row) {
		const divergencia = divergencias.get(row?.clienteId);
		if (!divergencia || divergencia?.erro) {
			return "Não foi possível checar divergência";
		}
		if (divergencia?.bloqueia) {
			return `Bloqueado por divergência: ${
				divergencia?.extrasNoCliente?.length || 0
			} item(ns) exclusivo(s) do cliente`;
		}
		return "";
	}

	// Par fundo/texto do status do evento de resultado - mesmos tokens de chip.background/colorText,
	// com corNeutraChip pra abortado_divergencia (mesmo par usado em "Ausente no cliente"/"Sem versão")
	function coresResultado(status) {
		if (status === "sucesso") {
			return {
				background: theme.palette.chip.background.success,
				color: theme.palette.chip.colorText.success,
			};
		}
		if (status === "falha") {
			return {
				background: theme.palette.chip.background.critical,
				color: theme.palette.chip.colorText.critical,
			};
		}
		return corNeutraChip(theme);
	}

	const nomePorCliente = new Map(
		(clientesDesatualizados || []).map((cliente) => [cliente?.clienteId, cliente?.nome]),
	);

	// Filtragem por nome, sempre disponivel (sem limiar de tamanho da lista)
	const clientesFiltrados = (clientesDesatualizados || []).filter((cliente) =>
		cliente?.nome?.toLowerCase()?.includes(busca.toLowerCase()),
	);

	const rows = clientesFiltrados.map((cliente) => ({
		...cliente,
		id: cliente?.clienteId,
	}));

	const columns = [
		{ field: "nome", headerName: "Cliente", flex: 1, minWidth: 160 },
		{
			field: "versaoCliente",
			headerName: "Versão atual",
			width: 150,
			renderCell: (params) => params.row?.versaoCliente || "—",
		},
		{
			field: "bloqueado",
			headerName: "Bloqueado",
			width: 170,
			renderCell: (params) =>
				bloqueado(params.row) ? (
					<Tooltip title={motivoBloqueio(params.row)}>
						<Chip
							size="small"
							label="Bloqueado"
							sx={{
								...styles.chipBloqueado,
								backgroundColor: theme.palette.chip.background.high,
								color: theme.palette.chip.colorText.high,
							}}
						/>
					</Tooltip>
				) : null,
		},
	];

	// Grid de clientes desatualizados, com selecao via checkbox nativo - extraido pra nao duplicar
	// as options entre o caso "com altura fixa" e o caso "altura automatica" (poucos clientes)
	const gridClientes = (
		<DatagridComponent
			rows={rows}
			columns={columns}
			options={{
				checkboxSelection: true,
				disableRowSelectionOnClick: true,
				isRowSelectable: (params) => !bloqueado(params.row) && !enviando,
				rowSelectionModel: selecionados,
				onRowSelectionModelChange: (novaSelecao) => {
					setSelecionados(novaSelecao);
					setConfirmado(false);
				},
			}}
		/>
	);

	return (
		<Dialog open={open} onClose={handleFechar} fullWidth maxWidth="sm">
			<DialogTitle>Forçar atualização — {item?.nome || item?.chave}</DialogTitle>
			<DialogContent>
				<Typography variant="body2" sx={styles.subtitulo}>
					Versão de destino: {item?.versaoReferencia || "—"}
				</Typography>
				{carregandoDivergencias && (
					<Box sx={styles.loadingContainer}>
						<CircularProgress size={28} />
					</Box>
				)}
				{!carregandoDivergencias && resumo === null && (
					<Box>
						<TextField
							size="small"
							placeholder="Buscar cliente..."
							value={busca}
							onChange={(event) => setBusca(event.target.value)}
							sx={styles.buscaField}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search fontSize="small" />
									</InputAdornment>
								),
							}}
						/>
						{/* Paper outlined (borda desenhada, sem sombra) - so esta tabela, diferente do padrao com elevation usado em abaTemplates/Clientes/Staff/Home */}
						<Paper variant="outlined" sx={styles.paper}>
							{/* Altura fixa so quando o grid tem mais de 4 linhas - mesmo limiar do autoHeight interno
							do DatagridComponent, senao o Box força altura mesmo com poucos clientes desatualizados */}
							{rows.length > 4 ? (
								<Box sx={styles.gridContainer}>{gridClientes}</Box>
							) : (
								gridClientes
							)}
						</Paper>
						<Box sx={styles.confirmacaoContainer}>
							<FormControlLabel
								control={
									<Checkbox
										checked={confirmado}
										disabled={enviando}
										onChange={(event) => setConfirmado(event.target.checked)}
									/>
								}
								label={`Confirmo que quero atualizar os ${selecionados.length} cliente(s) selecionado(s)`}
							/>
						</Box>
						{erroEnvio && (
							<Typography variant="body2" sx={styles.erro}>
								{erroEnvio}
							</Typography>
						)}
					</Box>
				)}
				{!carregandoDivergencias && resumo !== null && (
					<Box sx={styles.resultadoContainer}>
						{resumo.length === 0 && (
							<Typography variant="body2" sx={styles.semItens}>
								Nenhum cliente foi processado.
							</Typography>
						)}
						{resumo.map((linha) => {
							const cor = coresResultado(linha?.status);
							return (
								<Box key={linha?.clienteId} sx={styles.linhaResultado}>
									<Typography variant="body2" sx={styles.nomeResultado}>
										{nomePorCliente.get(linha?.clienteId) || linha?.clienteId}
									</Typography>
									<Chip
										size="small"
										label={linha?.status}
										sx={{
											...styles.chip,
											backgroundColor: cor.background,
											color: cor.color,
										}}
									/>
									{linha?.status !== "sucesso" && linha?.motivo && (
										<Typography variant="caption" sx={styles.motivoResultado}>
											{linha.motivo}
										</Typography>
									)}
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{resumo === null ? (
					<>
						<Button onClick={handleFechar} disabled={enviando}>
							Cancelar
						</Button>
						<Tooltip
							title={
								!enviando && (!confirmado || selecionados.length === 0)
									? "Selecione ao menos um cliente e marque a confirmação abaixo para habilitar"
									: ""
							}
							disableHoverListener={enviando || (confirmado && selecionados.length > 0)}
						>
							<span>
								<Button
									variant="contained"
									disabled={enviando || !confirmado || selecionados.length === 0}
									onClick={handleConfirmarAtualizacao}
								>
									{enviando ? <CircularProgress size={20} /> : "Confirmar atualização"}
								</Button>
							</span>
						</Tooltip>
					</>
				) : (
					<Button variant="contained" onClick={handleFechar}>
						Fechar
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
